import React, { useState, useRef } from 'react';
import { ExternalLink, Eye, Calendar, Tag } from 'lucide-react';
import ScrollReveal from '../animations/ScrollReveal';
import Tilt3DCard from '../animations/Tilt3DCard';
import MagneticButton from '../animations/MagneticButton';
import { Link } from 'react-router-dom';

interface Project {
  id: number;
  title: string;
  description: string;
  category: string;
  tags: string[];
  liveUrl: string;
  featured: boolean;
  year: string;
  gradient: string;
}

const projects: Project[] = [
  {
    id: 1,
    title: 'Dentist Office Website',
    description: 'Clean, modern dental practice website with appointment booking, services showcase, patient testimonials, and mobile-responsive design.',
    category: 'web-design',
    tags: ['React', 'Tailwind CSS', 'Responsive', 'Healthcare'],
    liveUrl: 'https://dentist.imodernize.dev',
    featured: true,
    year: '2026',
    gradient: 'from-blue-500 to-cyan-500',
  },
  {
    id: 2,
    title: 'Law Firm Website',
    description: 'Professional law firm website featuring practice areas, attorney profiles, case results, and a secure client consultation form.',
    category: 'web-design',
    tags: ['React', 'Tailwind CSS', 'Professional', 'Legal'],
    liveUrl: 'https://law-firm.imodernize.dev',
    featured: true,
    year: '2026',
    gradient: 'from-gray-700 to-gray-900',
  },
  {
    id: 3,
    title: 'Med Spa & Aesthetics',
    description: 'Elegant med spa website showcasing treatments, before/after galleries, online booking, and luxurious branding that attracts high-end clients.',
    category: 'web-design',
    tags: ['React', 'Tailwind CSS', 'Luxury', 'Beauty'],
    liveUrl: 'https://med-spa.imodernize.dev',
    featured: false,
    year: '2026',
    gradient: 'from-pink-400 to-rose-500',
  },
  {
    id: 4,
    title: 'HVAC Services',
    description: 'High-converting HVAC company website with service areas, emergency contact features, seasonal promotions, and customer reviews.',
    category: 'web-design',
    tags: ['React', 'Tailwind CSS', 'Local Business', 'HVAC'],
    liveUrl: 'https://hvac.imodernize.dev',
    featured: false,
    year: '2026',
    gradient: 'from-orange-500 to-red-500',
  },
  {
    id: 5,
    title: 'Plumber Website',
    description: 'Fast-loading plumbing company site with service listings, emergency call-to-action, pricing transparency, and local SEO optimization.',
    category: 'web-design',
    tags: ['React', 'Tailwind CSS', 'Local SEO', 'Trades'],
    liveUrl: 'https://plumber.imodernize.dev',
    featured: false,
    year: '2026',
    gradient: 'from-blue-600 to-blue-800',
  },
  {
    id: 6,
    title: 'Roofing Company',
    description: 'Bold roofing contractor website with project galleries, free estimate forms, financing options, and storm damage assessment tools.',
    category: 'web-design',
    tags: ['React', 'Tailwind CSS', 'Contractor', 'Roofing'],
    liveUrl: 'https://roofing.imodernize.dev',
    featured: false,
    year: '2026',
    gradient: 'from-amber-600 to-amber-800',
  },
  {
    id: 7,
    title: 'Landscaping Business',
    description: 'Beautiful landscaping company website featuring project portfolios, seasonal services, client testimonials, and instant quote requests.',
    category: 'web-design',
    tags: ['React', 'Tailwind CSS', 'Outdoor', 'Landscaping'],
    liveUrl: 'https://landscaping.imodernize.dev',
    featured: false,
    year: '2026',
    gradient: 'from-green-500 to-emerald-600',
  },
  {
    id: 8,
    title: 'Therapy Practice',
    description: 'Warm, welcoming therapist website with practitioner bios, specialties, online scheduling, and a calming design that puts visitors at ease.',
    category: 'web-design',
    tags: ['React', 'Tailwind CSS', 'Wellness', 'Therapy'],
    liveUrl: 'https://therapy.imodernize.dev',
    featured: false,
    year: '2026',
    gradient: 'from-teal-400 to-teal-600',
  },
  {
    id: 9,
    title: 'Physical Therapy Clinic',
    description: 'Dynamic physical therapy website showcasing specializations, patient success stories, insurance info, and easy appointment booking.',
    category: 'web-design',
    tags: ['React', 'Tailwind CSS', 'Healthcare', 'PT'],
    liveUrl: 'https://physical-therapy.imodernize.dev',
    featured: false,
    year: '2026',
    gradient: 'from-indigo-500 to-indigo-700',
  },
];

const PortfolioShowcase: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  const featuredProjects = projects.filter(project => project.featured);

  return (
    <section className="relative py-24 overflow-hidden bg-gray-50">
      <div className="absolute inset-0 bg-gradient-to-b from-white via-gray-50 to-white" />

      <div className="container-custom relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-5 py-2 rounded-full text-sm font-semibold mb-4 bg-blue-50 text-blue-700 border border-blue-100">
            <Eye className="w-4 h-4 mr-2" />
            Portfolio Showcase
          </div>
          
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-gray-900">
            Our Latest
            <span className="block text-blue-600">Work</span>
          </h2>
          
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
            Explore our portfolio of professional websites built for real businesses 
            across different industries — all designed to convert visitors into customers.
          </p>
        </div>

        {/* Featured Projects */}
        <div className="mb-20">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Featured Projects</h3>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {featuredProjects.map((project) => (
              <div 
                key={project.id}
                className="group relative bg-white rounded-2xl overflow-hidden border border-gray-200 hover:border-blue-300 hover:shadow-xl transition-all duration-300"
              >
                {/* Project preview */}
                <div className={`relative h-80 overflow-hidden bg-gradient-to-br ${project.gradient}`}>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center text-white">
                      <div className="text-6xl font-bold opacity-20 mb-2">{project.title.charAt(0)}</div>
                      <p className="text-white/80 text-lg font-medium">{project.liveUrl.replace('https://', '')}</p>
                    </div>
                  </div>
                  
                  {/* Overlay on hover */}
                  <div className="absolute inset-0 bg-blue-600/90 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <a
                      href={project.liveUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center px-6 py-3 bg-white text-gray-900 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      View Live Site
                    </a>
                  </div>

                  {/* Featured badge */}
                  <div className="absolute top-4 left-4">
                    <div className="px-3 py-1 bg-blue-600 text-white text-sm font-bold rounded-lg shadow-lg">
                      Featured
                    </div>
                  </div>
                </div>

                {/* Project details */}
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-gray-500 text-sm flex items-center">
                      <Calendar className="w-4 h-4 mr-1" />
                      {project.year}
                    </span>
                    <a href={project.liveUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 text-sm font-semibold hover:underline">
                      {project.liveUrl.replace('https://', '')}
                    </a>
                  </div>

                  <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                    {project.title}
                  </h3>
                  
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {project.description}
                  </p>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2">
                    {project.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-blue-50 hover:text-blue-700 transition-colors"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* All Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" ref={containerRef}>
          {projects.filter(p => !p.featured).map((project, index) => (
            <ScrollReveal key={project.id} direction="up" delay={0.2 + index * 0.1}>
              <Tilt3DCard>
                <div 
                  className="group relative bg-white rounded-2xl overflow-hidden border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all duration-300"
                >
                  {/* Project preview */}
                  <div className={`relative h-48 overflow-hidden bg-gradient-to-br ${project.gradient}`}>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <p className="text-white/80 text-sm font-medium">{project.liveUrl.replace('https://', '')}</p>
                    </div>
                    
                    {/* Overlay on hover */}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center">
                      <a
                        href={project.liveUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-3 bg-white text-gray-900 rounded-full hover:bg-gray-100 transition-colors"
                      >
                        <ExternalLink className="w-5 h-5" />
                      </a>
                    </div>

                    {/* Category badge */}
                    <div className="absolute top-3 left-3">
                      <div className="px-2 py-1 bg-black/50 backdrop-blur-sm text-white text-xs rounded-full flex items-center">
                        <Tag className="w-3 h-3 mr-1" />
                        Web Design
                      </div>
                    </div>
                  </div>

                  {/* Project details */}
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-gray-400 text-sm">{project.year}</span>
                      <a href={project.liveUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 text-xs font-medium hover:underline">
                        Visit Site →
                      </a>
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                      {project.title}
                    </h3>
                    
                    <p className="text-gray-600 mb-4 text-sm leading-relaxed line-clamp-2">
                      {project.description}
                    </p>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-1">
                      {project.tags.slice(0, 3).map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded"
                        >
                          {tag}
                        </span>
                      ))}
                      {project.tags.length > 3 && (
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                          +{project.tags.length - 3}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </Tilt3DCard>
            </ScrollReveal>
          ))}
        </div>

        {/* Call to Action */}
        <div className="text-center mt-20">
          <ScrollReveal direction="up" delay={0.8}>
            <p className="text-gray-600 mb-8 text-lg">
              Ready to get a website like these for your business?
            </p>
            <MagneticButton magneticStrength={25}>
              <Link
                to="/contact"
                className="inline-block px-8 py-4 bg-blue-600 text-white font-bold rounded-full hover:bg-blue-700 transition-all duration-300 text-lg shadow-lg hover:shadow-xl"
              >
                Free Consultation
              </Link>
            </MagneticButton>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
};

export default PortfolioShowcase;